import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ApparelProduct } from "@/types/models";
import ApparelService from "@/services/apparelService";
import { getCurrentOrgId } from "@/lib/auth";
import { ProductManagementHeader } from "@/components/apparel/management/ProductManagementHeader";
import { ProductManagementContent } from "@/components/apparel/management/ProductManagementContent";
import { AddProductDialog } from "@/components/apparel/management/dialogs/AddProductDialog";
import { EditProductDialog } from "@/components/apparel/management/dialogs/EditProductDialog";
import { DeleteProductDialog } from "@/components/apparel/management/dialogs/DeleteProductDialog";
import { motion } from "framer-motion";

const ApparelManagement: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<ApparelProduct[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ApparelProduct | null>(null);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const orgId = await getCurrentOrgId();
      if (!orgId) {
        toast.error("Unable to determine organization");
        return;
      }
      
      const productData = await ApparelService.getProducts(orgId);
      setProducts(productData);
      
      const categoryData = await ApparelService.getProductCategories();
      setCategories(categoryData);
    } catch (error) {
      console.error("Error loading products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const handleAddProduct = () => setIsAddDialogOpen(true);
  const handleEditProduct = (product: ApparelProduct) => {
    setSelectedProduct(product);
    setIsEditDialogOpen(true);
  };
  const handleDeleteProduct = (product: ApparelProduct) => {
    setSelectedProduct(product);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedProduct) return;
    
    try {
      const success = await ApparelService.updateProduct(selectedProduct.id, {
        status: "archived"
      });
      if (success) {
        toast.success("Product archived successfully");
        await loadProducts();
      } else {
        toast.error("Failed to archive product");
      }
    } catch (error) {
      console.error("Error archiving product:", error);
      toast.error("Failed to archive product");
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedProduct(null);
    }
  };

  const handleProductCreated = async () => {
    setIsAddDialogOpen(false);
    await loadProducts();
  };

  const handleProductUpdated = async () => {
    setIsEditDialogOpen(false);
    setSelectedProduct(null);
    await loadProducts();
  };

  return (
    <motion.div
      className="max-w-7xl mx-auto px-4 md:px-6 py-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        className="rounded-3xl shadow-2xl border border-slate-200/50 bg-gradient-to-br from-white via-slate-50/30 to-white backdrop-blur-xl overflow-hidden"
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-8 text-white">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold mb-2">Manage Catalog</h1>
              <p className="text-blue-100 text-lg">
                Create and manage your apparel and merchandise collection
              </p>
            </div>
            <motion.button
              onClick={handleAddProduct}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white px-6 py-3 rounded-xl font-semibold shadow-lg transition-all duration-300 flex items-center gap-2 hover:scale-105"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="text-xl">+</span>
              Add Product
            </motion.button>
          </div>
        </div>
        <div className="p-8">
          <ProductManagementContent
            products={products}
            categories={categories}
            loading={loading}
            onEdit={handleEditProduct}
            onDelete={handleDeleteProduct}
          />
        </div>
      </motion.div>
      <AddProductDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        categories={categories}
        onSuccess={handleProductCreated}
      />
      <EditProductDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        product={selectedProduct}
        categories={categories}
        onSuccess={handleProductUpdated}
      />
      <DeleteProductDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        product={selectedProduct}
        onConfirm={confirmDelete}
      />
    </motion.div>
  );
};

export default ApparelManagement;
